import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { type ImageStyle } from 'expo-image';
import { BodyText, Button, FallbackImage } from '@sg/garnish';

import { ScanToPayEmptyStateIllustration } from '../../assets';
import {
  emptyStylePalettes,
  illustrationPalettes,
  type ScanToPayPalette,
  textPalettes,
} from '../../utils';

export const ScanToPayEmptyState = (props: ScanToPayEmptyStateProps) => {
  const { palette, imageUrl, body, cta, onAddCard } = props;
  const { formatMessage } = useIntl();

  const illustrationStyles = [
    styles.illustration,
    illustrationPalettes[palette] as ImageStyle,
  ];

  return (
    <View style={[styles.container, emptyStylePalettes[palette]]}>
      <FallbackImage
        baseUrl={imageUrl}
        contentfulOptions={contentfulOptions}
        defaultImage={ScanToPayEmptyStateIllustration}
        style={illustrationStyles}
        aria-label={formatMessage(messages.a11yLabel)}
      />

      <View style={styles.content}>
        <BodyText
          style={[styles.body, textPalettes[palette]]}
          sizeMatch={['16']}
        >
          {body}
        </BodyText>

        <Button
          size="large"
          palette={palette === 'cream' ? 'spinach' : 'light'}
          onPress={onAddCard}
        >
          {cta}
        </Button>
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const contentfulOptions = {
  fm: 'webp' as const,
  fit: 'fill' as const,
  w: 343,
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Scan to pay empty state',
    description: 'Scan To Pay | Empty State | A11y Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    borderRadius: theme.radius.large,
  },
  content: {
    flex: 1,
    justifyContent: 'space-around',
    gap: theme.spacing['10'],
    paddingHorizontal: theme.spacing['8'],
    marginBottom: theme.spacing['10'],
  },
  body: {
    textAlign: 'center',
    color: theme.colors.KALE,
    marginTop: theme.spacing['10'],
  },
  illustration: {
    flex: 1,
    minHeight: 157,
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayEmptyStateProps = {
  palette: ScanToPayPalette;
  imageUrl?: string;
  body: string;
  cta: string;
  onAddCard: () => void;
};
